import type { ReplayPlugin } from "@slot/client-engine";
import type { ScatterHighlightEffect } from "@slot/presentation";
import { createFeatureAudioPlugin } from "../audio/integration/FeatureAudioPlugin";
import type { CoreServices } from "../boot/CoreServices";
import { createFeatureHudPlugin } from "../feature-hud/FeatureHudPlugin";
import { createFeatureModePlugin } from "../feature-mode/FeatureModePlugin";
import { createBigWinPlugin } from "../overlays/big-win/BigWinPlugin";
import type { BigWinPort } from "../overlays/big-win/BigWinPort";
import { BigWinPresenter } from "../overlays/big-win/BigWinPresenter";
import type { BigWinTuning } from "../overlays/big-win/BigWinTuning";
import { createFeatureFinishedPlugin } from "../overlays/feature-finished/FeatureFinishedPlugin";
import type { FeatureFinishedPort } from "../overlays/feature-finished/FeatureFinishedPort";
import { FeatureFinishedPresenter } from "../overlays/feature-finished/FeatureFinishedPresenter";
import type { FeatureFinishedTuning } from "../overlays/feature-finished/FeatureFinishedTuning";
import { createFeatureTriggerPlugin } from "../overlays/feature-trigger/FeatureTriggerPlugin";
import type { FeatureTriggerPort } from "../overlays/feature-trigger/FeatureTriggerPort";
import { FeatureTriggerPresenter } from "../overlays/feature-trigger/FeatureTriggerPresenter";
import type { FeatureTriggerTuning } from "../overlays/feature-trigger/FeatureTriggerTuning";
import type { GameTransitionPort } from "../overlays/game-transition/GameTransitionPort";
import { GameTransitionPresenter } from "../overlays/game-transition/GameTransitionPresenter";
import { createAutoAdvanceHoldPlugin } from "../spin/AutoAdvanceHoldPlugin";
import { SpinBoundaryPacing } from "../spin/SpinBoundaryPacing";
import { createSpinBoundaryPlugin } from "../spin/SpinBoundaryPlugin";
import { createSpinCyclePlugin } from "../spin/SpinCyclePlugin";
import { createCurrentWinPlugin } from "../win/current-win/CurrentWinPlugin";
import { createScatterPlugin } from "../win/sequence/ScatterPlugin";
import { createWinCollectPlugin } from "../win/sequence/WinCollectPlugin";
import { createWinCyclePlugin } from "../win/sequence/WinCyclePlugin";
import { createWinHighlightPlugin } from "../win/sequence/WinHighlightPlugin";
import type { WinSequencePresenter } from "../win/sequence/WinSequencePresenter";
import type { RoundLifecycle } from "./RoundLifecycle";

/** What a title brings to the shell's half of the round: its panels and their values, its grid's pieces, its features. */
export interface ShellRoundInput {
  services: CoreServices;
  /** Settlement and feature buys; null in replay mode. */
  lifecycle: RoundLifecycle | null;
  /** The title's overlay views behind the shell's presenters. */
  panels: { featureTrigger: FeatureTriggerPort; featureFinished: FeatureFinishedPort; bigWin: BigWinPort; gameTransition: GameTransitionPort };
  tuning: {
    featureTrigger: FeatureTriggerTuning;
    featureFinished: FeatureFinishedTuning;
    bigWin: BigWinTuning;
    /** The hold before an auto-advanced spin (free spins, autospin): after a win, after a round without one. */
    autoAdvance: { postWinHoldSeconds: number; postRoundHoldSeconds: number };
  };
  winSequence: WinSequencePresenter;
  /** The scatter collect's pulse; null → the title presents no `present_scatter_state`. */
  scatterHighlightEffect: ScatterHighlightEffect | null;
  /** Before every reel spin (the click's pre-spin and every spin boundary): the title's teardown and reel start. */
  beginSpinCycle: () => void;
  /** Each spin's start, after the win sequence's reset: the title's own per-spin state. */
  onSpinStart?: () => void;
  /**
   * A win that stays on the grid, cleared at a feature's exit before its finished panel (decision 65): the title's
   * whole clear (its highlight's `clearStandingWin`, its strokes, its overlays). Absent: nothing is cleared there.
   */
  clearStandingWin?: () => void;
  /** The mechanic's replay plugin (the grid's actions). */
  mechanic: ReplayPlugin;
  /** The title's own plugins, after the mechanic, in their order (slot-cannon: the cannon, the chance detonation). */
  features: readonly ReplayPlugin[];
}

/**
 * The shell's half of the round, built in its one order (boundary review 2026-10-01 #9, decision 46): the overlay
 * presenters over the title's panels, and the replay plugins with the title's mechanic and features in their slots.
 * The engine runs the plugins that react to one moment in list order, which is what this pins:
 * - a spin start: the boundary cue → the current win armed → the spin cycle (the title's teardown and reels, the win
 *   sequence reset, the meter hidden);
 * - a spin end: the boundary cue → the HUD count-up → the current win set → the auto-advance hold → the idle win
 *   re-show (decision 65);
 * - a feature state (enter / update / exit): the HUD → the meter hidden on exit → the current win (parked on exit) →
 *   the standing win cleared on exit → the feature mode → the finished panel → the audio cue → the trigger panel;
 * - then the big win, the win highlight, the scatter collect, the mechanic, the title's features.
 * Until decision 46 every title listed these itself, and a title that left one out silently lost part of the bar.
 */
export function createShellRoundPlugins(input: ShellRoundInput): readonly ReplayPlugin[] {
  const s = input.services;
  const transition = new GameTransitionPresenter({ panel: input.panels.gameTransition, audioService: s.audioService, canvasOverlayStore: s.canvasOverlayStore, log: s.log });
  const featureTrigger = new FeatureTriggerPresenter({
    canvasOverlayStore: s.canvasOverlayStore,
    panel: input.panels.featureTrigger,
    audioService: s.audioService,
    interactionBlockStore: s.interactionBlockStore,
    log: s.log,
    tuning: input.tuning.featureTrigger,
    runner: s.chromeTaskRunner,
  });
  const lifecycle = input.lifecycle;
  const featureFinished = new FeatureFinishedPresenter({
    canvasOverlayStore: s.canvasOverlayStore,
    panel: input.panels.featureFinished,
    audioService: s.audioService,
    betSystem: s.betSystem,
    formatter: s.moneyContext.formatter,
    localization: s.localization,
    log: s.log,
    tuning: input.tuning.featureFinished,
    extraSpinsOfferFeatureIds: s.initialSettings.extraSpinsOfferFeatureIds,
    featurePriceMultipliersById: s.initialSettings.featurePriceMultipliersById,
    extraSpinsOfferCountById: s.initialSettings.extraSpinsOfferCountById,
    onBuyExtraSpins: lifecycle ? (featureId) => lifecycle.buyFeature(featureId) : undefined,
    runner: s.chromeTaskRunner,
  });
  const bigWin = new BigWinPresenter({
    canvasOverlayStore: s.canvasOverlayStore,
    panel: input.panels.bigWin,
    audioService: s.audioService,
    hapticsService: s.hapticsService,
    interactionBlockStore: s.interactionBlockStore,
    money: s.moneyContext,
    log: s.log,
    tuning: input.tuning.bigWin,
    bigWinTiers: s.initialSettings.bigWinTiers ?? [],
    runner: s.chromeTaskRunner,
  });
  const spinBoundaryPacing = new SpinBoundaryPacing({ log: s.log, runner: s.presentationTaskRunner, boundaryPaceMilliseconds: 0 });
  const winSequence = input.winSequence;
  return [
    createSpinBoundaryPlugin({ audioService: s.audioService, spinBoundaryPacing }),
    createFeatureHudPlugin({ presenter: s.featureHudPresenter }),
    createWinCollectPlugin({ presenter: winSequence }),
    createCurrentWinPlugin({ spinBoundary: s.currentWinSpinBoundary }),
    createSpinCyclePlugin({ beginSpinCycle: input.beginSpinCycle, presenter: winSequence, onSpinStart: input.onSpinStart }),
    createAutoAdvanceHoldPlugin({
      featureStateStore: s.featureStateStore,
      autoSpinStore: s.autoSpinStore,
      runner: s.presentationTaskRunner,
      postWinHoldSeconds: input.tuning.autoAdvance.postWinHoldSeconds,
      postRoundHoldSeconds: input.tuning.autoAdvance.postRoundHoldSeconds,
    }),
    createWinCyclePlugin({ presenter: winSequence, clearStandingWin: input.clearStandingWin }),
    createFeatureModePlugin({ store: s.featureModeStore }),
    createFeatureFinishedPlugin({ presenter: featureFinished, transition, lifecycle }),
    createFeatureAudioPlugin({ audioService: s.audioService }),
    createFeatureTriggerPlugin({ presenter: featureTrigger, featureStateStore: s.featureStateStore, transition }),
    createBigWinPlugin({ presenter: bigWin }),
    createWinHighlightPlugin({ presenter: winSequence }),
    ...(input.scatterHighlightEffect
      ? [createScatterPlugin({ audioService: s.audioService, hapticsService: s.hapticsService, presenter: winSequence, scatterHighlightEffect: input.scatterHighlightEffect })]
      : []),
    input.mechanic,
    ...input.features,
  ];
}
